// Turnus + Turnusstart eines Kunden festlegen — an der Haustür und in der
// Kundenkarte dasselbe Blatt.
//
// Der Rhythmus (jede Woche / alle 2 / 3 / 4) und der START des Rhythmus liegen
// pro Kunde individuell. Der Fahrer verschiebt den Start bewusst: „Kunde war im
// Urlaub, ab HEUTE zählt der Turnus neu." Das läuft über eine eigene Server-
// Aktion (useTurnusSetzen), die den Anker verschiebt UND die nächsten Tage
// abgleicht — nicht über das normale Kunde-Speichern, das den Anker einfriert.

import { useEffect, useRef, useState } from "react";
import {
  useAboTakt,
  useTurnusSetzen,
  naechsteLieferung,
  turnusText,
  WEEKDAY_LABEL,
} from "@/lib/subscriptions";
import { Button, Card, Field, Segmented, Sheet, Spinner } from "@/design-system/ui";

/** Lokales Heute als JJJJ-MM-TT (nicht UTC — sonst kippt es um Mitternacht). */
function heuteIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function fmtTag(iso: string): string {
  return new Date(iso + "T12:00:00").toLocaleDateString("de-DE", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}

const INTERVALLE = [
  { value: 1, label: "Jede Woche" },
  { value: 2, label: "Alle 2 Wo." },
  { value: 3, label: "Alle 3 Wo." },
  { value: 4, label: "Alle 4 Wo." },
];

export function TurnusSheet({
  customerId,
  customerName,
  onClose,
}: {
  customerId: string;
  customerName?: string;
  onClose: () => void;
}) {
  const { data: takt, isLoading } = useAboTakt(customerId);
  const setzen = useTurnusSetzen();

  const [intervall, setIntervall] = useState(1);
  const [startsOn, setStartsOn] = useState(heuteIso());
  const [fehler, setFehler] = useState<string | null>(null);
  const geladen = useRef(false);

  useEffect(() => {
    if (!takt || geladen.current) return;
    geladen.current = true;
    setIntervall(takt.interval_weeks);
    // Vorschlag: ab heute. Liegt der Start schon in der Zukunft, den behalten.
    setStartsOn(takt.starts_on > heuteIso() ? takt.starts_on : heuteIso());
  }, [takt]);

  const speichern = async () => {
    setFehler(null);
    try {
      await setzen.mutateAsync({ customerId, intervalWeeks: intervall, startsOn });
      onClose();
    } catch (e) {
      setFehler((e as Error).message || "Konnte nicht gespeichert werden.");
    }
  };

  const vorschau =
    takt && takt.weekdays.length
      ? naechsteLieferung({ weekdays: takt.weekdays, interval_weeks: intervall, starts_on: startsOn }, startsOn > heuteIso() ? startsOn : heuteIso())
      : null;

  const tage = takt?.weekdays?.length
    ? [...takt.weekdays].sort().map((w) => WEEKDAY_LABEL[w]).join(", ")
    : null;

  return (
    <Sheet
      open
      onClose={onClose}
      title={customerName ? `Turnus — ${customerName}` : "Turnus"}
      footer={
        <div className="space-y-2">
          {fehler && (
            <p className="rounded-lg border border-danger-bg/30 bg-danger-bg/10 px-3 py-2 text-sm text-danger-fg">
              {fehler}
            </p>
          )}
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            disabled={!takt || !takt.weekdays.length}
            loading={setzen.isPending}
            onClick={speichern}
          >
            Turnus speichern
          </Button>
        </div>
      }
    >
      {isLoading ? (
        <div className="flex justify-center py-10 text-ink-400">
          <Spinner />
        </div>
      ) : !takt || !takt.weekdays.length ? (
        <p className="text-[15px] text-ink-300">
          Dieser Kunde hat noch keinen Liefertag. Lege erst über den Korb einen
          Wochentag an — danach lässt sich der Turnus festlegen.
        </p>
      ) : (
        <div className="space-y-6">
          <p className="text-sm text-ink-400">
            Wie oft {tage ? `(${tage})` : ""} beliefert wird und ab wann der
            Rhythmus zählt. Ab dem Startdatum wird neu gerechnet — für
            Urlaubsrückkehrer und neue Kunden.
          </p>

          <Field label="Rhythmus">
            <Segmented value={intervall} onChange={setIntervall} options={INTERVALLE} />
          </Field>

          <Field label="Turnus startet ab" hint="Ab diesem Tag zählt der Rhythmus neu.">
            <input
              type="date"
              value={startsOn}
              onChange={(e) => setStartsOn(e.target.value || heuteIso())}
              className="h-11 w-full rounded-lg border border-ink-700 bg-ink-900 px-3 text-base tabular-nums text-ink-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60"
            />
          </Field>

          <Card className="p-4">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-ink-400">Nächste Lieferung</span>
              <span className="text-[15px] font-semibold tabular-nums text-ink-50">
                {vorschau ? fmtTag(vorschau) : "—"}
              </span>
            </div>
            <p className="mt-1 text-sm text-ink-500">{turnusText(intervall)}</p>
          </Card>
        </div>
      )}
    </Sheet>
  );
}

/**
 * Kompakte Turnus-Karte für die Kundenkarte: zeigt Rhythmus + nächste Lieferung
 * und öffnet auf Knopfdruck das Blatt zum Ändern. Nichts, wenn der Kunde kein
 * aktives Abo hat.
 */
export function TurnusCard({ customerId, customerName }: { customerId: string; customerName?: string }) {
  const { data: takt } = useAboTakt(customerId);
  const [offen, setOffen] = useState(false);

  if (!takt) return null;

  const tage = [...(takt.weekdays ?? [])].sort().map((w) => WEEKDAY_LABEL[w]).join(", ");
  const naechste = takt.weekdays.length ? naechsteLieferung(takt, heuteIso()) : null;

  return (
    <Card className="p-4">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-xs font-medium uppercase tracking-wider text-ink-400">Turnus</h2>
        <Button size="sm" onClick={() => setOffen(true)}>
          Ändern
        </Button>
      </div>
      <p className="text-[15px] text-ink-200">
        {turnusText(takt.interval_weeks)}
        {tage ? ` · ${tage}` : ""}
      </p>
      <p className="mt-0.5 text-sm text-ink-500">
        Nächste Lieferung: {naechste ? fmtTag(naechste) : "—"}
      </p>

      {offen && (
        <TurnusSheet customerId={customerId} customerName={customerName} onClose={() => setOffen(false)} />
      )}
    </Card>
  );
}
